import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, Subject } from 'rxjs';
import { map, scan, take, withLatestFrom } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AuthService } from '../../../../../adreno-common/src/lib/auth';
import { valuesOf } from '../../../../../adreno-common/src/lib/utils';
import { Action } from '../code';
import { ActionDto } from '../models';
import { WorkService } from '../services';
import { DiallerService } from '@app/dialler/services';

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styles: [
    `
      .notes {
        max-width: 25vw;
      }

      .modal-body {
        height: 50vh;
      }

      textarea {
        resize: none;
      }

      :host {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryComponent implements OnInit, OnDestroy {
  private note = new Subject<string>();
  private subs = new SubSink();
  formGroup: FormGroup;
  viewModel$: Observable<ViewModel>;

  constructor(
    private workSvc: WorkService,
    private modalSvc: NgbModal,
    private authSvc: AuthService,
    private diallerSvc: DiallerService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.initFormGroup();
    this.viewModel$ = this.initViewModel();
  }

  private initFormGroup() {
    return this.fb.group({
      notes: this.fb.control(undefined),
      clientNotes: this.fb.control(undefined),
    });
  }

  private initValueChanges() {
    this.subs.sink = this.workSvc.action$.pipe(take(1)).subscribe((action) =>
      this.formGroup.patchValue({
        notes: action?.notes,
        clientNotes: action?.clientNotes,
      })
    );
    this.subs.sink = valuesOf<FormModel>(this.formGroup).subscribe((model) =>
      this.workSvc.updateNotes(model!.notes, model!.clientNotes)
    );
  }

  private initViewModel() {
    return merge<Partial<ViewModel>>(
      this.workSvc.history$.pipe(
        withLatestFrom(this.workSvc.master$),
        map(([history, master]) => ({
          actions: history,
          attempt:
            (history?.filter(
              (act) =>
                act.actionType === 'OB' && act.campaignId === master!.campaignId
            ).length || 0) + 1,
        }))
      ),
      this.note.pipe(map((note) => ({ note }))),
      this.workSvc.action$.pipe(map((a) => ({ currentaction: a }))),
      this.authSvc.userId$.pipe(map((uid) => ({ currentuser: uid }))),
      this.workSvc.testMode$.pipe(map((testFlag) => ({ testMode: testFlag }))),
      this.diallerSvc.submitting.pipe(map((submitting) => ({ submitting }))),
      this.workSvc.obLaunched$.pipe(map((obLaunched) => ({ obLaunched }))),
      this.workSvc.ibLaunched$.pipe(map((ibLaunched) => ({ ibLaunched }))),
      this.workSvc.closeDisable$.pipe(
        map((closeDisable) => ({ closeDisable }))
      ),
      this.workSvc.master$.pipe(
        withLatestFrom(this.workSvc.scheduledCampaigns$),
        map(([master, scheduledCampaigns]) => ({
          ib: scheduledCampaigns
            ?.find((c) => c.campaignId === master?.campaignId)
            ?.actionType.startsWith('IB'),
        }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  closeIB() {
    this.diallerSvc.closeIB();
  }

  closeOB() {
    this.diallerSvc.closeOB();
  }

  next() {
    this.diallerSvc.next();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initValueChanges();
  }

  showNote(template: any, note: string) {
    this.note.next(note);
    this.modalSvc.open(template, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      size: 'xl',
    });
  }
}

interface ViewModel {
  actions: ActionDto[] | undefined;
  attempt: number;
  closeDisable: boolean | undefined;
  currentaction: Action | undefined;
  currentuser: number;
  ib: boolean | undefined;
  ibLaunched: boolean | undefined;
  note: string;
  obLaunched: boolean | undefined;
  submitting: boolean;
  testMode: boolean | undefined;
}

interface FormModel {
  clientNotes: string;
  notes: string;
}
